import React, { useMemo } from 'react';
import { Alert, Divider, Modal, Spin, Typography } from 'antd';
import dayjs from 'dayjs';
import StandardTable from '../common/StandardTable';
import { useGetPrimaveraTaskSmallByCode, useGetTkfTaskResources } from '../../hooks/genericHooks';
import type { PrimaveraTaskSmall, VwTkfTaskResources } from '../../types';

const { Text } = Typography;

export interface ActivityDetailsShowProps {
  open: boolean;
  taskCode?: string | null;
  onClose: () => void;
}

const toNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const unwrapRows = <T,>(resp: unknown): T[] => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp as T[];
  const obj = resp as { data?: unknown; rows?: unknown; recordset?: unknown };
  if (Array.isArray(obj.data)) return obj.data as T[];
  if (Array.isArray((obj.data as { data?: unknown } | undefined)?.data)) {
    return (obj.data as { data: T[] }).data;
  }
  if (Array.isArray(obj.rows)) return obj.rows as T[];
  if (Array.isArray(obj.recordset)) return obj.recordset as T[];
  return [];
};

const formatDate = (v: unknown): string => {
  if (!v) return '-';
  const d = dayjs(String(v));
  return d.isValid() ? d.format('DD/MM/YYYY') : String(v);
};

const fmtQty = (v: unknown): string => {
  const n = toNum(v);
  if (n == null) return '';
  return n.toLocaleString(undefined, { maximumFractionDigits: 6 });
};

const fmtMoney = (v: number): string => {
  return v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const budgetedCostOf = (row: VwTkfTaskResources): number => {
  const stored = toNum(row.target_cost);
  if (stored != null) return stored;
  return (toNum(row.target_qty) ?? 0) * (toNum(row.cost_per_qty) ?? 0);
};

const infoPaneStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  borderTop: '1px solid #f0f0f0',
  borderLeft: '1px solid #f0f0f0',
};

const infoCellStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '148px minmax(0, 1fr)',
  borderRight: '1px solid #f0f0f0',
  borderBottom: '1px solid #f0f0f0',
  minWidth: 0,
};

const infoLabelStyle: React.CSSProperties = {
  padding: '8px 12px',
  background: '#fafafa',
  borderRight: '1px solid #f0f0f0',
};

const infoValueStyle: React.CSSProperties = {
  padding: '8px 12px',
  minWidth: 0,
  overflowWrap: 'anywhere',
};

const actualCostOf = (row: VwTkfTaskResources): number => {
  const actual = toNum(row.act_reg_qty) ?? 0;
  const target = toNum(row.target_qty) ?? 0;
  const qty = Math.min(actual, target);
  return qty * (toNum(row.cost_per_qty) ?? 0);
};

const ActivityDetailsShow: React.FC<ActivityDetailsShowProps> = ({ open, taskCode, onClose }) => {
  const code = open && taskCode != null && String(taskCode).trim() !== '' ? String(taskCode).trim() : null;

  const {
    data: taskResp,
    isLoading: isLoadingTask,
    isFetching: isFetchingTask,
    error: taskError,
  } = useGetPrimaveraTaskSmallByCode(code);

  const activity = useMemo(() => unwrapRows<PrimaveraTaskSmall>(taskResp)[0] ?? null, [taskResp]);
  const taskId = activity?.task_id != null ? Number(activity.task_id) : null;

  const {
    data: resourceResp,
    isLoading: isLoadingResources,
    error: resourceError,
  } = useGetTkfTaskResources(open ? taskId : null);

  const resources = useMemo(() => unwrapRows<VwTkfTaskResources>(resourceResp), [resourceResp]);

  const totalCost = useMemo(
    () => resources.reduce((sum, row) => sum + budgetedCostOf(row), 0),
    [resources]
  );
  const actualCost = useMemo(
    () => resources.reduce((sum, row) => sum + actualCostOf(row), 0),
    [resources]
  );

  const loadingTask = Boolean(code) && (isLoadingTask || isFetchingTask) && !activity;
  const title = activity
    ? `${activity.task_code} — ${activity.task_name}`
    : 'Activity details';

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onClose}
      footer={null}
      width={800}
      zIndex={1200}
      destroyOnClose
    >
      {!code ? (
        <Text type="secondary">No activity code was provided.</Text>
      ) : loadingTask ? (
        <Spin />
      ) : taskError ? (
        <Alert type="error" showIcon message={(taskError as Error)?.message || 'Failed to load activity'} />
      ) : !activity ? (
        <Text type="secondary">No activity was found for {code}.</Text>
      ) : (
        <>
          <div style={infoPaneStyle}>
            {[
              { label: 'Status', value: activity.status_code || '-' },
              {
                label: 'Progress %',
                value: toNum(activity.phys_complete_pct) == null
                  ? '-'
                  : `${(toNum(activity.phys_complete_pct) ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`,
              },
              { label: 'Total Cost', value: fmtMoney(totalCost) },
              { label: 'Actual Cost', value: fmtMoney(actualCost) },
              { label: 'Target Start', value: formatDate(activity.target_start_date) },
              { label: 'Target Finish', value: formatDate(activity.target_end_date) },
              { label: 'Actual Start', value: formatDate(activity.act_start_date) },
              { label: 'Actual End', value: formatDate(activity.act_end_date) },
            ].map((item) => (
              <div key={item.label} style={infoCellStyle}>
                <div style={infoLabelStyle}>{item.label}</div>
                <div style={infoValueStyle}>{item.value}</div>
              </div>
            ))}
          </div>

          <Divider style={{ margin: '12px 0' }} />

          {resourceError ? (
            <Alert type="error" showIcon message={(resourceError as Error)?.message || 'Failed to load resources'} />
          ) : (
            <StandardTable
              dataSource={resources}
              rowKey={(r: VwTkfTaskResources) => String(r.taskrsrc_id)}
              loading={isLoadingResources}
              bordered
              enableExport
              exportFileName={`activity_${activity.task_code}_resources`}
              pagination={{ pageSize: 20, showSizeChanger: true }}
              columns={[
                { title: 'Res.Code', dataIndex: 'rsrc_short_name', key: 'rsrc_short_name', width: 110 },
                { title: 'Unit', dataIndex: 'unit_name', key: 'unit_name', width: 90 },
                { title: 'Resource', dataIndex: 'rsrc_name', key: 'rsrc_name', width: 200 },
                { title: 'Type', dataIndex: 'rsrc_type', key: 'rsrc_type', width: 70 },
                {
                  title: 'Cost/Qty',
                  dataIndex: 'cost_per_qty',
                  key: 'cost_per_qty',
                  width: 90,
                  align: 'right' as const,
                  render: (v: unknown) => fmtQty(v),
                },
                {
                  title: 'Target Qty',
                  dataIndex: 'target_qty',
                  key: 'target_qty',
                  width: 90,
                  align: 'right' as const,
                  render: (v: unknown) => fmtQty(v),
                },
                {
                  title: 'Actual Qty',
                  dataIndex: 'act_reg_qty',
                  key: 'act_reg_qty',
                  width: 90,
                  align: 'right' as const,
                  render: (v: unknown) => fmtQty(v),
                },
                {
                  title: 'Rem. Qty',
                  dataIndex: 'remain_qty',
                  key: 'remain_qty',
                  width: 90,
                  align: 'right' as const,
                  render: (v: unknown) => fmtQty(v),
                },
              ]}
            />
          )}
        </>
      )}
    </Modal>
  );
};

export default ActivityDetailsShow;
